import { CommonModule } from '@angular/common'
import { Component, HostListener, OnDestroy, Optional, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { FigureService } from 'src/app/service/data/figure.service'
import { NoteService } from 'src/app/service/data/note.service'
import { PairService } from 'src/app/service/data/pair.service'
import { QuizService } from 'src/app/service/data/quiz.service'
import { WordService } from 'src/app/service/data/word.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { SearchCommand, SearchParams } from '../../commands/search.command'
import {
  DataSearchResult,
  DaydicTypesIconNames,
  Media,
  SearchChips,
  SearchTypes,
  Themes,
} from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { DdcDesktopDialogComponent } from '../ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcMobileSheetComponent } from '../ddc-mobile-sheet/ddc-mobile-sheet.component'
import { DdcPairDialogComponent } from '../ddc-pair-dialog/ddc-pair-dialog.component'
import { DdcSearchResultDictionaryComponent } from '../ddc-search-result-dictionary/ddc-search-result-dictionary.component'
import { DdcSearchResultFigureComponent } from '../ddc-search-result-figure/ddc-search-result-figure.component'
import { DdcSearchResultNoteComponent } from '../ddc-search-result-note/ddc-search-result-note.component'
import { DdcSearchResultPairComponent } from '../ddc-search-result-pair/ddc-search-result-pair.component'
import { DdcSearchResultQuizComponent } from '../ddc-search-result-quiz/ddc-search-result-quiz.component'
import { DdcSearchResultUserComponent } from '../ddc-search-result-user/ddc-search-result-user.component'
import { DdcSearchResultWordComponent } from '../ddc-search-result-word/ddc-search-result-word.component'
import { DdcWordDialogComponent } from '../ddc-word-dialog/ddc-word-dialog.component'

@Component({
  selector: 'ddc-general-search-dialog',
  templateUrl: './ddc-general-search-dialog.component.html',
  styleUrl: './ddc-general-search-dialog.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    AngularMaterialsModules,
    DdcSearchResultWordComponent,
    DdcSearchResultPairComponent,
    DdcSearchResultUserComponent,
    DdcSearchResultDictionaryComponent,
    DdcSearchResultNoteComponent,
    DdcSearchResultQuizComponent,
    DdcSearchResultFigureComponent,
  ],
})
export class DdcGeneralSearchDialogComponent implements OnDestroy {
  _searchTerm: string = ''
  _loading: boolean = false
  _isInputActive = false
  _selectedType: SelectOption = null // Updated to store the entire type object
  types: SelectOption[] = []

  // Theme properties
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  private themeSubscription: Subscription

  // Search subject for debouncing
  private searchSubject = new Subject<{ query: string; type: string }>()

  displayedColumns: string[] = ['type', 'label', 'info', 'time_since_creation']
  _iconNames = DaydicTypesIconNames
  _theme$: Observable<string>

  selectedRowIndexWord = -1
  selectedRowIndexPair = -1
  selectedRowIndexDic = -1
  selectedRowIndexCat = -1
  selectedRowIndexTag = -1
  selectedRowIndexAll = -1
  selectedRowIndexUser = -1
  selectedRowIndexNote = -1
  selectedRowIndexQuiz = -1
  selectedRowIndexFigure = -1

  _DefaultProfilePic: string =
    'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'

  _assets = Media.ASSETS
  _searchTypeWord = SearchChips.WORD
  _searchTypePair = SearchChips.PAIR
  _searchTypeDic = SearchChips.DICTIONARY
  _searchTypeCat = SearchChips.CATEGORY
  _searchTypeTag = SearchChips.TAG
  _searchTypeUser = SearchChips.USER

  _showWordSearchResults: boolean = false
  _showPairSearchResults: boolean = false
  _showDicSearchResults: boolean = false
  _showCatSearchResults: boolean = false
  _showTagSearchResults: boolean = false
  _showAllSearchResults: boolean = false
  _showUserSearchResults: boolean = false
  _showNoteSearchResults: boolean = false
  _showQuizSearchResults: boolean = false
  _showFigureSearchResults: boolean = false

  // search results
  wordSearchDataSource: MatTableDataSource<DataSearchResult>
  pairSearchDataSource: MatTableDataSource<DataSearchResult>
  dicSearchDataSource: MatTableDataSource<DataSearchResult>
  catSearchDataSource: MatTableDataSource<DataSearchResult>
  tagSearchDataSource: MatTableDataSource<DataSearchResult>
  allSearchDataSource: MatTableDataSource<DataSearchResult>
  userSearchDataSource: MatTableDataSource<DataSearchResult>
  noteSearchDataSource: MatTableDataSource<DataSearchResult>
  quizSearchDataSource: MatTableDataSource<DataSearchResult>
  figureSearchDataSource: MatTableDataSource<DataSearchResult>

  _wordSearchResults: DataSearchResult[] = []
  _pairSearchResults: DataSearchResult[] = []
  _dicSearchResults: DataSearchResult[] = []
  _catSearchResults: DataSearchResult[] = []
  _tagSearchResults: DataSearchResult[] = []
  _allSearchResults: DataSearchResult[] = []
  _userSearchResults: DataSearchResult[] = []
  _noteSearchResults: DataSearchResult[] = []
  _quizSearchResults: DataSearchResult[] = []
  _figureSearchResults: DataSearchResult[] = []

  _isMobile = false

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 500
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Only handle keyboard events if this dialog is active (focused)
    const searchInput = document.querySelector(
      '.search-palette-container input[matInput]',
    ) as HTMLElement
    const activeElement = document.activeElement

    // Check if the active element is within our component
    const isOurComponent =
      searchInput && (activeElement === searchInput || searchInput.contains(activeElement))

    if (!isOurComponent) {
      return
    }

    // Only handle keyboard navigation when we have search results
    if (this._searchTerm && this._searchTerm.length >= 2 && this.hasResults()) {
      this.handleKeyDown(this._selectedType, event)
    } else if (event.key === 'Escape') {
      // Always handle escape key to close dialog
      if (this.dialogRef) {
        this.dialogRef.close()
      }
      event.preventDefault()
    }
  }

  ngOnInit(): void {
    this.checkIfMobile()
    this._selectedType = { raw_value: 'All', view_value: 'All', icon: 'select_all' }

    // Auto-focus on the search input after a short delay
    setTimeout(() => {
      const searchInput = document.querySelector('input[matInput]') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe()
    }
  }

  /**
   * Setup debounced search to avoid too many API calls
   */
  setupSearchDebouncing(): void {
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after the user stops typing
        distinctUntilChanged((prev, curr) => prev.query === curr.query && prev.type === curr.type),
        switchMap(({ query, type }): any => {
          if (!query || query.length < 2) {
            this.clearSearchResults()
            this.hideAllSearchResults()
            return []
          }

          this._loading = true
          const searchParams: SearchParams = {
            query,
            type: type as any,
            maxResults: 10,
          }

          return this.searchCommand.execute(searchParams)
        }),
      )
      .subscribe({
        next: results => {
          this.handleSearchResults(results)
        },
        error: error => {
          this.logger.error('DdcGeneralSearchDialogComponent: Search error:', error)
          this._loading = false
          this.clearSearchResults()
        },
      })
  }

  /**
   * Handle search results from the search command
   */
  handleSearchResults(results: any): void {
    this._loading = false

    // Apply highlighting to search results
    if (results.words) {
      this._wordSearchResults = this.searchCommand.highlightSearchTerms(
        results.words,
        this._searchTerm,
      )
      this.wordSearchDataSource.data = this._wordSearchResults
    }

    if (results.pairs) {
      this._pairSearchResults = this.searchCommand.highlightSearchTerms(
        results.pairs,
        this._searchTerm,
      )
      this.pairSearchDataSource.data = this._pairSearchResults
    }

    if (results.dictionaries) {
      this._dicSearchResults = this.searchCommand.highlightSearchTerms(
        results.dictionaries,
        this._searchTerm,
      )
      this.dicSearchDataSource.data = this._dicSearchResults
    }

    if (results.users) {
      this._userSearchResults = this.searchCommand.highlightSearchTerms(
        results.users,
        this._searchTerm,
      )
      this.userSearchDataSource.data = this._userSearchResults
    }

    if (results.notes) {
      this._noteSearchResults = this.searchCommand.highlightSearchTerms(
        results.notes,
        this._searchTerm,
      )
      this.noteSearchDataSource.data = this._noteSearchResults
    }

    if (results.quizzes) {
      this._quizSearchResults = this.searchCommand.highlightSearchTerms(
        results.quizzes,
        this._searchTerm,
      )
      this.quizSearchDataSource.data = this._quizSearchResults
    }

    if (results.figures) {
      this._figureSearchResults = this.searchCommand.highlightSearchTerms(
        results.figures,
        this._searchTerm,
      )
      this.figureSearchDataSource.data = this._figureSearchResults
    }

    if (results.all) {
      this._allSearchResults = this.searchCommand.highlightSearchTerms(
        results.all,
        this._searchTerm,
      )
      this.allSearchDataSource.data = this._allSearchResults
    }

    // Update visibility flags
    this._showWordSearchResults = results.words && results.words.length > 0
    this._showPairSearchResults = results.pairs && results.pairs.length > 0
    this._showDicSearchResults = results.dictionaries && results.dictionaries.length > 0
    this._showUserSearchResults = results.users && results.users.length > 0
    this._showNoteSearchResults = results.notes && results.notes.length > 0
    this._showQuizSearchResults = results.quizzes && results.quizzes.length > 0
    this._showFigureSearchResults = results.figures && results.figures.length > 0
    this._showAllSearchResults = results.all && results.all.length > 0

    // Auto-select first result for keyboard navigation
    this.initializeSelection()
  }

  // Method to handle search term changes
  onSearchTermChange(event: Event): void {
    const target = event.target as HTMLInputElement
    this._searchTerm = target.value
    if (this._searchTerm && this._searchTerm.length >= 2) {
      this.onSearchChange(this._searchTerm)
    }
  }

  // Method to select a type (for tabs)
  selectType(typeName: string): void {
    const type = this.types.find(t => t.raw_value === typeName)
    if (type) {
      this._selectedType = type
      this.resetSelectedIndices()
      // Initialize selection for the new tab if there are results
      this.initializeSelection()
      // Trigger search again with current term if we have one
      if (this._searchTerm && this._searchTerm.length >= 2) {
        this.onSearchChange(this._searchTerm)
      }
    }
  }

  // Get current results based on selected type
  getCurrentResults(): DataSearchResult[] {
    if (!this._selectedType) return []

    switch (this._selectedType.raw_value) {
      case 'All':
        return this._allSearchResults
      case 'Word':
        return this._wordSearchResults
      case 'Pair':
        return this._pairSearchResults
      case 'Dictionary':
        return this._dicSearchResults
      case 'User':
        return this._userSearchResults
      case 'Note':
        return this._noteSearchResults
      case 'Quiz':
        return this._quizSearchResults
      case 'Figure':
        return this._figureSearchResults
      default:
        return []
    }
  }

  // Select a result item
  selectResult(index: number): void {
    const results = this.getCurrentResults()
    if (results && results[index]) {
      this.enterEvent(this._selectedType, index)
    }
  }

  // Get result icon based on type
  getResultIcon(type: string): string {
    switch (type) {
      case 'word':
        return this._iconNames.WORD
      case 'pair':
        return this._iconNames.PAIR
      case 'dictionary':
        return this._iconNames.DICTIONARY
      case 'user':
        return this._iconNames.PERSON
      case 'note':
        return this._iconNames.NOTE
      case 'quiz':
        return this._iconNames.QUIZ
      case 'figure':
        return this._iconNames.FIGURE
      default:
        return 'help'
    }
  }

  // Open all results
  openAllResults(): void {
    if (this._selectedType && this._searchTerm) {
      // Close the dialog first
      if (this.dialogRef) {
        this.dialogRef.close()
      }

      // Navigate to the search page with search parameters
      const tabMap = {
        Word: 'words',
        Pair: 'pairs',
        Dictionary: 'dictionaries',
        User: 'users',
        Note: 'notes',
        Quiz: 'quizzes',
        Figure: 'figures',
        All: 'words', // Default to words for "All"
      }

      const tab = tabMap[this._selectedType.raw_value as keyof typeof tabMap] || 'words'

      this.router.navigate(['/search'], {
        queryParams: {
          q: this._searchTerm,
          tab: tab,
        },
      })
    }
  }

  openReadWordDialog(index: number): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading word details...')

    // Get word by id
    this.word_service.get_word(index).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          const clickedSearchedWord = res.res
          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()

            // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-word', element: clickedSearchedWord }
            const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-word', element: clickedSearchedWord }
            const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
            dialogRef.afterClosed().subscribe(result => {
              if (result === 'word') {
              }
            })
          }
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading word:', error)
        // You could show an error dialog here
      },
    })
  }

  openReadPairDialog(index: number): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading pair details...')

    // Get pair by id
    this.pair_service.get_pair(index).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          const clickedSearchedPair = res.res

          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()

            // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-pair', element: clickedSearchedPair }
            const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-pair', element: clickedSearchedPair }
            const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
            dialogRef.afterClosed().subscribe(result => {
              if (result === 'pair') {
              }
            })
          }
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading pair:', error)
        // You could show an error dialog here
      },
    })
  }

  getResultCount(type: string): number {
    switch (type) {
      case 'All':
        return this._allSearchResults.length
      case 'Word':
        return this._wordSearchResults.length
      case 'Pair':
        return this._pairSearchResults.length
      case 'Dictionary':
        return this._dicSearchResults.length
      case 'User':
        return this._userSearchResults.length
      case 'Note':
        return this._noteSearchResults.length
      case 'Quiz':
        return this._quizSearchResults.length
      case 'Figure':
        return this._figureSearchResults.length
      default:
        return 0
    }
  }

  getTotalResultsCount(): number {
    return (
      this._wordSearchResults.length +
      this._pairSearchResults.length +
      this._dicSearchResults.length +
      this._userSearchResults.length +
      this._noteSearchResults.length +
      this._quizSearchResults.length +
      this._figureSearchResults.length
    )
  }

  hasResults(): boolean {
    return this.getCurrentResults().length > 0
  }

  checkIfMobile(): void {
    if (window.innerWidth < 500) {
      // 768px is a common breakpoint for mobile devices
      this._isMobile = true
    } else {
      this._isMobile = false
    }
  }

  constructor(
    private logger: DdcLoggerService,
    @Optional() public dialogRef: MatDialogRef<DdcGeneralSearchDialogComponent>,
    public dialog: MatDialog,
    private word_service: WordService,
    private pair_service: PairService,
    private note_service: NoteService,
    private quiz_service: QuizService,
    private figure_service: FigureService,
    private router: Router,
    private searchCommand: SearchCommand,
    public themeService: ThemeService,
    private store: Store,
  ) {
    this.wordSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.pairSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.dicSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.catSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.tagSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.userSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.noteSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.quizSearchDataSource = new MatTableDataSource<DataSearchResult>()
    this.figureSearchDataSource = new MatTableDataSource<DataSearchResult>()

    // Theme subscription
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this._theme$ = this.store.select(getTheme)

    this.getTypes()
    this.setupSearchDebouncing()
  }

  getTypes(): void {
    this.logger.info('DdcGeneralSearchDialogComponent getTypes()')

    const typesArray = Object.values(SearchTypes) as string[]

    // Loop through the enum values and create SelectOption objects
    for (const value of typesArray) {
      this.types.push({
        raw_value: value,
        view_value: value,
        icon: this.getIconForType(value), // Assign an icon based on the type
      })
    }
  }

  getIconForType(type: string): string {
    switch (type) {
      case 'All':
        return this._iconNames.ALL
      case 'User':
        return this._iconNames.PERSON
      case 'Word':
        return this._iconNames.WORD
      case 'Pair':
        return this._iconNames.PAIR
      case 'Dictionary':
        return this._iconNames.DICTIONARY
      case 'Note':
        return this._iconNames.NOTE
      case 'Quiz':
        return this._iconNames.QUIZ
      case 'Figure':
        return this._iconNames.FIGURE
      default:
        return 'help'
    }
  }

  openSearchResults(type: SelectOption, target: string): void {
    this.logger.info('DdcGeneralSearchDialogComponent openSearchResults()')

    if (this.dialogRef) {
      this.dialogRef.close()
    }

    let searchType = 0
    if (type.raw_value === 'Word') {
      searchType = this._searchTypeWord
    } else if (type.raw_value === 'Pair') {
      searchType = this._searchTypePair
    } else if (type.raw_value === 'Dictionary') {
      searchType = this._searchTypeDic
    } else if (type.raw_value === 'Category') {
      searchType = this._searchTypeCat
    } else if (type.raw_value === 'Tag') {
      searchType = this._searchTypeTag
    }

    this.router.navigate(['/search/'], {
      queryParams: { type: searchType, target },
    })
  }

  isRowHighlighted(i: number): boolean {
    if (this._selectedType.raw_value === 'Word') {
      return this.selectedRowIndexWord === i
    } else if (this._selectedType.raw_value === 'Pair') {
      return this.selectedRowIndexPair === i
    } else if (this._selectedType.raw_value === 'Dictionary') {
      return this.selectedRowIndexDic === i
    } else if (this._selectedType.raw_value === 'All') {
      return this.selectedRowIndexAll === i
    } else if (this._selectedType.raw_value === 'User') {
      return this.selectedRowIndexUser === i
    } else if (this._selectedType.raw_value === 'Note') {
      return this.selectedRowIndexNote === i
    } else if (this._selectedType.raw_value === 'Quiz') {
      return this.selectedRowIndexQuiz === i
    } else if (this._selectedType.raw_value === 'Figure') {
      return this.selectedRowIndexFigure === i
    } else return false
  }

  dynamicRowIcon(rowType: string): string {
    if (this._selectedType.raw_value === 'Word') {
      return this._iconNames.WORD
    } else if (this._selectedType.raw_value === 'Pair') {
      return this._iconNames.PAIR
    } else if (this._selectedType.raw_value === 'Dictionary') {
      return this._iconNames.DICTIONARY
    } else if (this._selectedType.raw_value === 'User') {
      return this._iconNames.PERSON
    } else if (this._selectedType.raw_value === 'Note') {
      return this._iconNames.NOTE
    } else if (this._selectedType.raw_value === 'Quiz') {
      return this._iconNames.QUIZ
    } else if (this._selectedType.raw_value === 'Figure') {
      return this._iconNames.FIGURE
    } else if (this._selectedType.raw_value === 'All') {
      if (rowType === 'word') {
        return this._iconNames.WORD
      } else if (rowType === 'pair') {
        return this._iconNames.PAIR
      } else if (rowType === 'dictionary') {
        return this._iconNames.DICTIONARY
      } else if (rowType === 'user') {
        return this._iconNames.PERSON
      } else if (rowType === 'note') {
        return this._iconNames.NOTE
      } else if (rowType === 'quiz') {
        return this._iconNames.QUIZ
      } else if (rowType === 'figure') {
        return this._iconNames.FIGURE
      } else return this._iconNames.ALL
    } else return this._iconNames.ALL
  }

  getRowType(): string {
    if (this._selectedType.raw_value === 'Word') {
      return 'word'
    } else if (this._selectedType.raw_value === 'Pair') {
      return 'pair'
    } else if (this._selectedType.raw_value === 'Dictionary') {
      return 'dic'
    } else if (this._selectedType.raw_value === 'User') {
      return 'user'
    } else if (this._selectedType.raw_value === 'Note') {
      return 'note'
    } else if (this._selectedType.raw_value === 'Quiz') {
      return 'quiz'
    } else if (this._selectedType.raw_value === 'Figure') {
      return 'figure'
    } else return 'all'
  }

  getDataSource(): DataSearchResult[] {
    this.logger.info('DdcGeneralSearchDialogComponent getDataSource()')

    if (this._selectedType.raw_value === 'Word') {
      return this._wordSearchResults
    } else if (this._selectedType.raw_value === 'Pair') {
      return this._pairSearchResults
    } else if (this._selectedType.raw_value === 'Dictionary') {
      return this._dicSearchResults
    } else if (this._selectedType.raw_value === 'All') {
      return this._allSearchResults
    } else if (this._selectedType.raw_value === 'User') {
      return this._userSearchResults
    } else if (this._selectedType.raw_value === 'Note') {
      return this._noteSearchResults
    } else if (this._selectedType.raw_value === 'Quiz') {
      return this._quizSearchResults
    } else if (this._selectedType.raw_value === 'Figure') {
      return this._figureSearchResults
    } else return this._allSearchResults
  }

  onSearchChange(searchValue: string): void {
    this._searchTerm = searchValue
    const type = this._selectedType ? this._selectedType.raw_value.toLowerCase() : 'all'

    // Reset all selected row indices when search changes
    this.resetSelectedIndices()

    // Trigger the debounced search
    this.searchSubject.next({ query: searchValue, type })
  }

  selectNextRow(type: string): void {
    this.logger.info('DdcGeneralSearchDialogComponent selectNextRow()')

    if (type == 'Word') {
      if (this.selectedRowIndexWord < this._wordSearchResults.length - 1) {
        this.selectedRowIndexWord++
      }
    } else if (type == 'Pair') {
      if (this.selectedRowIndexPair < this._pairSearchResults.length - 1) {
        this.selectedRowIndexPair++
      }
    } else if (type == 'Dictionary') {
      if (this.selectedRowIndexDic < this._dicSearchResults.length - 1) {
        this.selectedRowIndexDic++
      }
    } else if (type == 'User') {
      if (this.selectedRowIndexUser < this._userSearchResults.length - 1) {
        this.selectedRowIndexUser++
      }
    } else if (type == 'All') {
      if (this.selectedRowIndexAll < this._allSearchResults.length - 1) {
        this.selectedRowIndexAll++
      }
    } else if (type == 'Note') {
      if (this.selectedRowIndexNote < this._noteSearchResults.length - 1) {
        this.selectedRowIndexNote++
      }
    } else if (type == 'Quiz') {
      if (this.selectedRowIndexQuiz < this._quizSearchResults.length - 1) {
        this.selectedRowIndexQuiz++
      }
    } else if (type == 'Figure') {
      if (this.selectedRowIndexFigure < this._figureSearchResults.length - 1) {
        this.selectedRowIndexFigure++
      }
    }
  }

  selectPreviousRow(type: string): void {
    this.logger.info('DdcGeneralSearchDialogComponent selectPreviousRow() type:', type)

    if (type == 'Word') {
      if (this.selectedRowIndexWord > 0) {
        this.selectedRowIndexWord--
      }
    } else if (type == 'Pair') {
      if (this.selectedRowIndexPair > 0) {
        this.selectedRowIndexPair--
      }
    } else if (type == 'Dictionary') {
      if (this.selectedRowIndexDic > 0) {
        this.selectedRowIndexDic--
      }
    } else if (type == 'User') {
      if (this.selectedRowIndexUser > 0) {
        this.selectedRowIndexUser--
      }
    } else if (type == 'All') {
      if (this.selectedRowIndexAll > 0) {
        this.selectedRowIndexAll--
      }
    } else if (type == 'Note') {
      if (this.selectedRowIndexNote > 0) {
        this.selectedRowIndexNote--
      }
    } else if (type == 'Quiz') {
      if (this.selectedRowIndexQuiz > 0) {
        this.selectedRowIndexQuiz--
      }
    } else if (type == 'Figure') {
      if (this.selectedRowIndexFigure > 0) {
        this.selectedRowIndexFigure--
      }
    }
  }

  isRowSelected(i: number): boolean {
    if (this._selectedType.raw_value === 'Word') {
      return this.selectedRowIndexWord === i
    } else if (this._selectedType.raw_value === 'Pair') {
      return this.selectedRowIndexPair === i
    } else if (this._selectedType.raw_value === 'Dictionary') {
      return this.selectedRowIndexDic === i
    } else if (this._selectedType.raw_value === 'User') {
      return this.selectedRowIndexUser === i
    } else if (this._selectedType.raw_value === 'All') {
      return this.selectedRowIndexAll === i
    } else if (this._selectedType.raw_value === 'Note') {
      return this.selectedRowIndexNote === i
    } else if (this._selectedType.raw_value === 'Quiz') {
      return this.selectedRowIndexQuiz === i
    } else if (this._selectedType.raw_value === 'Figure') {
      return this.selectedRowIndexFigure === i
    } else return false
  }

  enterEvent(type: SelectOption, i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent enterEvent() type:', type.raw_value, 'i:', i)

    if (type.raw_value === 'Word') {
      this.openReadWordDialog(this._wordSearchResults[i].id)
    } else if (type.raw_value === 'Pair') {
      this.openReadPairDialog(this._pairSearchResults[i].id)
    } else if (type.raw_value === 'Dictionary') {
      this.navigateToDictionary(this._dicSearchResults[i])
    } else if (type.raw_value === 'User') {
      this.navigateToUser(this._userSearchResults[i])
    } else if (type.raw_value === 'Note') {
      this.navigateToNote(this._noteSearchResults[i])
    } else if (type.raw_value === 'Quiz') {
      this.navigateToQuiz(this._quizSearchResults[i])
    } else if (type.raw_value === 'Figure') {
      this.navigateToFigure(this._figureSearchResults[i])
    } else if (type.raw_value === 'All') {
      this.enterEventAll(i)
    }
  }

  enterEventPair(i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent | enterEventPair() i:', i)
    this.logger.info('this._pairSearchResults', this._pairSearchResults)
    this.logger.info('this._pairSearchResults[i]', this._pairSearchResults[i])

    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading pair details...')

    // ! get pair
    this.pair_service.get_pair(this._pairSearchResults[i].id).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          // close ddc-general-search-dialog first
          if (this.dialogRef) {
            this.dialogRef.close()
          }

          const clickedSearchedPair = res.res

          // ! open dialog
          const dialogRef = this.dialog.open(DdcPairDialogComponent, {
            data: {
              element: clickedSearchedPair,
              read_only: true,
            },
          })

          dialogRef.afterClosed().subscribe(result => {})
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading pair:', error)
      },
    })
  }

  clickEventPair(row: any): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading pair details...')

    // ! get pair
    this.pair_service.get_pair(row.id).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          // close ddc-general-search-dialog first
          this.dialogRef.close()

          const clickedSearchedPair = res.res

          // ! open dialog
          const dialogRef = this.dialog.open(DdcPairDialogComponent, {
            data: {
              element: clickedSearchedPair,
              read_only: true,
            },
          })

          dialogRef.afterClosed().subscribe(result => {})
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading pair:', error)
      },
    })
  }

  enterEventWord(i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent | enterEventWord() i:', i)
    this.logger.info('this._wordSearchResults', this._wordSearchResults)
    this.logger.info('this._wordSearchResults[i]', this._wordSearchResults[i])

    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading word details...')

    // ! get word
    this.word_service.get_word(this._wordSearchResults[i].id).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          this.dialogRef.close()
          const clickedSearchedWord = res.res
          const dialogRef = this.dialog.open(DdcWordDialogComponent, {
            data: {
              element: clickedSearchedWord,
              read_only: true,
            },
          })

          dialogRef.afterClosed().subscribe(result => {})
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading word:', error)
      },
    })
  }

  clickEventWord(row: any): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading word details...')

    this.word_service.get_word(row.id).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          this.dialogRef.close()
          const clickedSearchedWord = res.res
          const dialogRef = this.dialog.open(DdcWordDialogComponent, {
            data: {
              element: clickedSearchedWord,
              read_only: true,
            },
          })

          dialogRef.afterClosed().subscribe(result => {})
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading word:', error)
      },
    })
  }

  enterEventAll(i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent | enterEventAll() i:', i)
    this.logger.info('this._allSearchResults', this._allSearchResults)
    this.logger.info('this._allSearchResults[i]', this._allSearchResults[i])

    if (this._allSearchResults[i].type == 'word') {
      this.openReadWordDialog(this._allSearchResults[i].id)
    } else if (this._allSearchResults[i].type == 'pair') {
      this.openReadPairDialog(this._allSearchResults[i].id)
    } else if (this._allSearchResults[i].type == 'dictionary') {
      this.navigateToDictionary(this._allSearchResults[i])
    } else if (this._allSearchResults[i].type == 'user') {
      this.navigateToUser(this._allSearchResults[i])
    } else if (this._allSearchResults[i].type == 'note') {
      this.navigateToNote(this._allSearchResults[i])
    } else if (this._allSearchResults[i].type == 'quiz') {
      this.navigateToQuiz(this._allSearchResults[i])
    } else if (this._allSearchResults[i].type == 'figure') {
      this.navigateToFigure(this._allSearchResults[i])
    } else {
      this.logger.error('Unknown type:', this._allSearchResults[i].type)
    }
  }

  enterEventDic(i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent | enterEventDic() i:', i)
    this.logger.info('this._dicSearchResults', this._dicSearchResults)
    this.logger.info('this._dicSearchResults[i]', this._dicSearchResults[i])

    let clickedSearchedDic: any = this._dicSearchResults[i]
    this.dialogRef.close()
    this.router.navigate([
      '/library/dic/' + clickedSearchedDic.id + '/' + clickedSearchedDic.info.owner,
    ])
  }

  clickEventDic(row: any): void {
    // let clickedSearchedDic: any = this._dicSearchResults[i]
    // close ddc-general-search-dialog first
    this.dialogRef.close()
    this.router.navigate(['/library/dic/' + row.id + '/' + row.info.owner])
  }

  navigateToDictionary(item: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToDictionary() item:', item)
    // Close the dialog first
    if (this.dialogRef) {
      this.dialogRef.close()
    }

    // Handle different data structures for owner information
    let ownerUsername = ''
    if (item.info.owner && typeof item.info.owner === 'string') {
      // When owner is a string (Dictionary tab)
      ownerUsername = item.info.owner
    } else if (item.info.owner && item.info.owner.username) {
      // When owner is an object with username property (All tab)
      ownerUsername = item.info.owner.username
    } else {
      this.logger.error('Owner information not found or invalid:', item.info.owner)
      return
    }

    // Navigate to the dictionary page
    this.router.navigate(['/library/dic/' + item.id + '/' + ownerUsername])
  }

  navigateToUser(item: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToUser() item:', item)
    // Close the dialog first
    if (this.dialogRef) {
      this.dialogRef.close()
    }
    // Navigate to the user page
    this.router.navigate(['/users/' + item.info.username])
  }

  navigateToNote(item: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToNote() item:', item)
    this.openReadNoteDialog(item.id)
  }

  navigateToQuiz(item: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToQuiz() item:', item)
    this.openReadQuizDialog(item.id)
  }

  navigateToFigure(item: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToFigure() item:', item)
    this.openReadFigureDialog(item.id)
  }

  openReadNoteDialog(id: number): void {
    const loadingRef = this.showLoadingOverlay('Loading note details...')

    this.note_service.get_note(id).subscribe({
      next: (res: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res && res.res) {
          const element = res.res
          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]
            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-note', element }
            this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-note', element }
            this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
          }
        }
      },
      error: (error: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading note:', error)
      },
    })
  }

  openReadQuizDialog(id: number): void {
    const loadingRef = this.showLoadingOverlay('Loading quiz details...')

    this.quiz_service.get_quiz(id).subscribe({
      next: (res: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res && res.res) {
          const element = res.res
          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]
            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-quiz', element }
            this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-quiz', element }
            this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
          }
        }
      },
      error: (error: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading quiz:', error)
      },
    })
  }

  openReadFigureDialog(id: number): void {
    const loadingRef = this.showLoadingOverlay('Loading figure details...')

    this.figure_service.get_figure(id).subscribe({
      next: (res: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res && res.res) {
          const element = res.res
          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]
            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-figure', element }
            this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-figure', element }
            this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
          }
        }
      },
      error: (error: any) => {
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading figure:', error)
      },
    })
  }

  enterEventUser(i: number): void {
    this.logger.info('DdcGeneralSearchDialogComponent | enterEventDic() i:', i)
    this.logger.info('this._dicSearchResults', this._userSearchResults)
    this.logger.info('this._dicSearchResults[i]', this._userSearchResults[i])

    let clickedSearchedUser: any = this._userSearchResults[i]
    this.dialogRef.close()

    this.router.navigate(['/users/' + clickedSearchedUser.info.username])
  }

  handleKeyDown(type: SelectOption, event: KeyboardEvent): void {
    this.logger.info(
      'DdcGeneralSearchDialogComponent handleKeyDown() type:',
      type?.raw_value,
      'event:',
      event.key,
    )

    if (!type?.raw_value) {
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        this.selectNextRow(type.raw_value)
        event.preventDefault()
        break
      case 'ArrowUp':
        this.selectPreviousRow(type.raw_value)
        event.preventDefault()
        break
      case 'Enter':
        const selectedIndex = this.getSelectedRowIndex(type.raw_value)
        if (selectedIndex >= 0) {
          this.enterEvent(type, selectedIndex)
          event.preventDefault()
        }
        break
      case 'Escape':
        this.dialogRef.close()
        event.preventDefault()
        break
    }
  }

  private getSelectedRowIndex(type: string): number {
    switch (type) {
      case 'Word':
        return this.selectedRowIndexWord
      case 'Pair':
        return this.selectedRowIndexPair
      case 'Dictionary':
        return this.selectedRowIndexDic
      case 'User':
        return this.selectedRowIndexUser
      case 'All':
        return this.selectedRowIndexAll
      case 'Note':
        return this.selectedRowIndexNote
      case 'Quiz':
        return this.selectedRowIndexQuiz
      case 'Figure':
        return this.selectedRowIndexFigure
      default:
        return -1
    }
  }

  private resetSelectedIndices(): void {
    this.selectedRowIndexWord = -1
    this.selectedRowIndexPair = -1
    this.selectedRowIndexDic = -1
    this.selectedRowIndexCat = -1
    this.selectedRowIndexTag = -1
    this.selectedRowIndexAll = -1
    this.selectedRowIndexUser = -1
    this.selectedRowIndexNote = -1
    this.selectedRowIndexQuiz = -1
    this.selectedRowIndexFigure = -1
  }

  private initializeSelection(): void {
    // Auto-select first result based on current tab if there are results
    const currentResults = this.getCurrentResults()
    if (currentResults && currentResults.length > 0) {
      switch (this._selectedType?.raw_value) {
        case 'Word':
          this.selectedRowIndexWord = 0
          break
        case 'Pair':
          this.selectedRowIndexPair = 0
          break
        case 'Dictionary':
          this.selectedRowIndexDic = 0
          break
        case 'User':
          this.selectedRowIndexUser = 0
          break
        case 'All':
          this.selectedRowIndexAll = 0
          break
        case 'Note':
          this.selectedRowIndexNote = 0
          break
        case 'Quiz':
          this.selectedRowIndexQuiz = 0
          break
        case 'Figure':
          this.selectedRowIndexFigure = 0
          break
      }
    }
  }

  private clearSearchResults(): void {
    this._wordSearchResults = []
    this._pairSearchResults = []
    this._dicSearchResults = []
    this._catSearchResults = []
    this._tagSearchResults = []
    this._allSearchResults = []
    this._userSearchResults = []
    this._noteSearchResults = []
    this._quizSearchResults = []
    this._figureSearchResults = []
  }

  private hideAllSearchResults(): void {
    this._showWordSearchResults = false
    this._showPairSearchResults = false
    this._showDicSearchResults = false
    this._showCatSearchResults = false
    this._showTagSearchResults = false
    this._showUserSearchResults = false
    this._showNoteSearchResults = false
    this._showQuizSearchResults = false
    this._showFigureSearchResults = false
    this._loading = false
  }

  /**
   * Show a modern loading overlay
   */
  private showLoadingOverlay(message: string): any {
    // Set loading state to true to show spinner in UI
    this._loading = true

    // Create a simple loading overlay approach
    return {
      close: (): void => {
        // Hide loading overlay
        this._loading = false
      },
    }
  }
}
